/**
 * LocationViewerDialog — read-only map popup for a single clock punch.
 *
 * WHY (owner 2026-06-26): the worker /me/time-entries Clock records and the
 * admin /attendance/time-entries Clock Events only showed an address string
 * (often null) + a zone icon — a "黑盒". This dialog renders the actual punch
 * position as a marker, the geofence(s) as a translucent circle, and the
 * in/out-of-zone verdict, so a worker (and an admin) can SEE where a punch
 * landed relative to the allowed work area.
 *
 * Reuses the same Google Maps stack as GeofenceMapDialog. Read-only: no
 * dragging, no editing. Falls back to a coordinates readout when the Maps
 * API key isn't configured, so the dialog is never a blank box.
 *
 * Mobile twin: svdy-mobile clock-in / time-entries map (react-native-maps).
 * Both render the worker's punch + geofence circle from the SAME backend
 * data (MePunch.lat/lng/within_geofence + clock-in-config geofence.fences).
 */
import React from 'react';
import {
  Dialog, DialogContent, Box, Typography, IconButton, Chip,
} from '@mui/material';
import {
  CloseOutlined, CheckCircleOutlined, WarningAmberOutlined, GpsFixedOutlined,
} from '@mui/icons-material';
import { GoogleMap, CircleF, MarkerF } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import {
  GoogleMapsErrorBoundary,
  GoogleMapsUnavailableDialog,
  useSvdyGoogleMaps,
} from './googleMaps';

export interface ViewerFence {
  name?: string | null;
  lat: number;
  lng: number;
  radius_m: number;
}

interface LocationViewerDialogProps {
  open: boolean;
  onClose: () => void;
  lat: number | null | undefined;
  lng: number | null | undefined;
  withinGeofence?: boolean | null;
  address?: string | null;
  /** Horizontal GPS accuracy (m) at punch time — drawn as a faint ring. */
  accuracy?: number | null;
  /** Geofence circles to overlay (worker: clock-in-config fences; admin:
   *  org geofences). Empty/omitted → just the marker. */
  fences?: ViewerFence[];
  title?: string;
  subtitle?: string;
}

const MAP_CONTAINER = { width: '100%', height: 360 };

const LocationViewerDialogLoaded: React.FC<LocationViewerDialogProps> = ({
  open, onClose, lat, lng, withinGeofence, address, accuracy, fences, title, subtitle,
}) => {
  const { t } = useTranslation();
  const { isLoaded, loadError } = useSvdyGoogleMaps();
  const resolvedTitle = title ?? t('common:location.title');

  if (loadError) {
    return <GoogleMapsUnavailableDialog open title={resolvedTitle} onClose={onClose} />;
  }

  const hasCoords = lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng);
  const center = hasCoords ? { lat: lat as number, lng: lng as number } : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <Box sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.05rem' }}>
            {resolvedTitle}
          </Typography>
          {subtitle && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{subtitle}</Typography>}
        </Box>
        <IconButton aria-label={t('common:actions.close')} size="small" onClick={onClose}><CloseOutlined fontSize="small" /></IconButton>
      </Box>

      <DialogContent sx={{ pt: 0.5, pb: 2.5 }}>
        {/* Zone verdict */}
        {withinGeofence != null && (
          <Box sx={{ mb: 1.5 }}>
            {withinGeofence ? (
              <Chip
                icon={<CheckCircleOutlined sx={{ fontSize: 14 }} />}
                label={t('common:location.inZone')}
                size="small" color="success" variant="outlined"
                sx={{ borderRadius: 1, height: 22, fontSize: '0.72rem' }}
              />
            ) : (
              <Chip
                icon={<WarningAmberOutlined sx={{ fontSize: 14 }} />}
                label={t('common:location.outOfZone')}
                size="small" color="warning" variant="outlined"
                sx={{ borderRadius: 1, height: 22, fontSize: '0.72rem' }}
              />
            )}
          </Box>
        )}

        {!hasCoords ? (
          <Box sx={{
            p: 4, textAlign: 'center', borderRadius: 2,
            border: '1px solid', borderColor: 'divider', bgcolor: 'grey.50',
          }}>
            <GpsFixedOutlined sx={{ fontSize: 28, color: 'text.disabled', mb: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              {t('common:location.noLocation')}
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
              {isLoaded ? (
                <GoogleMap
                  mapContainerStyle={MAP_CONTAINER}
                  center={center as google.maps.LatLngLiteral}
                  zoom={16}
                  options={{
                    disableDefaultUI: true,
                    zoomControl: true,
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                    gestureHandling: 'cooperative',
                  }}
                >
                  <MarkerF position={center as google.maps.LatLngLiteral} />
                  {/* GPS accuracy ring (faint) */}
                  {accuracy != null && accuracy > 0 && (
                    <CircleF
                      center={center as google.maps.LatLngLiteral}
                      radius={accuracy}
                      options={{ fillColor: '#90A4AE', fillOpacity: 0.08, strokeColor: '#90A4AE', strokeOpacity: 0.4, strokeWeight: 1 }}
                    />
                  )}
                  {/* Geofence circles */}
                  {(fences ?? []).map((f, i) => (
                    <CircleF
                      key={`${f.lat}-${f.lng}-${i}`}
                      center={{ lat: f.lat, lng: f.lng }}
                      radius={f.radius_m}
                      options={{
                        fillColor: withinGeofence === false ? '#ED6C02' : '#2E7D32',
                        fillOpacity: 0.1,
                        strokeColor: withinGeofence === false ? '#ED6C02' : '#2E7D32',
                        strokeOpacity: 0.7,
                        strokeWeight: 2,
                      }}
                    />
                  ))}
                </GoogleMap>
              ) : (
                <Box sx={{ height: 360, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                  <Typography variant="body2" color="text.secondary">{t('common:location.loadingMap')}</Typography>
                </Box>
              )}
            </Box>

            {/* Address + coordinates readout (always shown — works even without the map) */}
            <Box sx={{ mt: 1.5, display: 'grid', gridTemplateColumns: '88px 1fr', rowGap: 0.75, alignItems: 'center' }}>
              {address && (
                <>
                  <Typography variant="caption" color="text.secondary">{t('common:location.address')}</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>{address}</Typography>
                </>
              )}
              <Typography variant="caption" color="text.secondary">{t('common:location.coordinates')}</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>
                {(lat as number).toFixed(6)}, {(lng as number).toFixed(6)}
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const LocationViewerDialog: React.FC<LocationViewerDialogProps> = (props) => {
  if (!props.open) return null;
  return (
    <GoogleMapsErrorBoundary
      fallback={(
        <GoogleMapsUnavailableDialog
          open
          title={props.title ?? 'Punch location'}
          onClose={props.onClose}
        />
      )}
    >
      <LocationViewerDialogLoaded {...props} />
    </GoogleMapsErrorBoundary>
  );
};

export default LocationViewerDialog;
